import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { FormSchema } from '../../types/form';
import { DynamicField } from './components/DynamicField';
import { formsApi } from '../../services/api';
import { showError } from '../../utils/toast';
import { evaluateConditionalLogic } from './utils/conditionalLogic';
import { evaluateCalculation } from './utils/calculations';

// ─── File helpers ─────────────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<{ name: string; type: string; size: number; data: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      name: file.name,
      type: file.type,
      size: file.size,
      data: reader.result as string,
    });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function fileListToBase64(files: FileList) {
  const results = [];
  for (let i = 0; i < files.length; i++) {
    results.push(await fileToBase64(files[i]));
  }
  return results.length === 1 ? results[0] : results;
}

export interface PublicFormPageProps {
  formSchemaProp?: FormSchema;
  handlerProp?: string;
}

const PublicFormPage: React.FC<PublicFormPageProps> = ({ formSchemaProp, handlerProp }) => {
  const routeParams = useParams<{ handler: string }>();
  const handler = handlerProp || routeParams.handler;

  const [formSchema, setFormSchema] = useState<FormSchema | null>(formSchemaProp || null);
  const [loading, setLoading] = useState(!formSchemaProp);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Capture UTM parameters and prefill values
  const [trackingData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
      referrer: document.referrer || undefined,
    };
  });

  // Prefill values from URL params (e.g. ?name=John&email=john@example.com)
  const [prefillValues] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const ignoredKeys = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'key']);
    const prefill: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!ignoredKeys.has(key)) prefill[key] = value;
    });
    return prefill;
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  useEffect(() => {
    if (formSchemaProp) {
      setFormSchema(formSchemaProp);
      setLoading(false);
      return;
    }

    const fetchForm = async () => {
      if (!handler) return;

      try {
        const params = new URLSearchParams(window.location.search);
        const shareKey = params.get('key');

        if (shareKey) {
          const response = await formsApi.verifyShareKey(shareKey.trim());
          if (response.success && response.form) {
            setFormSchema(response.form);
          } else {
            setError(response.message || 'Invalid share key');
          }
        } else {
          const data = await formsApi.getByHandler(handler);
          
          if (!data) {
            setError('Form not found');
            return;
          }

          setFormSchema(data);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [handler]);

  // Apply prefill values once form is loaded
  useEffect(() => {
    if (formSchema && Object.keys(prefillValues).length > 0) {
      Object.entries(prefillValues).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [formSchema, prefillValues, setValue]);

  const currentFields = formSchema?.isMultiStep
    ? formSchema.steps[currentStep]?.fields || []
    : formSchema?.fields || [];

  const watchedValues = watch();

  // Split fields: hidden (static) vs visible (conditional logic applied)
  const hiddenFields = currentFields.filter((field) => field.hidden);
  const visibleFields = currentFields.filter(
    (field) => !field.hidden && evaluateConditionalLogic(field.conditionalLogic, watchedValues)
  );

  // Keep hidden field values in sync with react-hook-form
  // Calculated hidden fields re-evaluate whenever any form value changes
  useEffect(() => {
    hiddenFields.forEach((field) => {
      if (field.type === 'calculated' && field.calculation) {
        const result = evaluateCalculation(field.calculation, watchedValues);
        if (result !== null) {
          setValue(field.name, result);
        }
      } else {
        // Only set default value once (don't overwrite if already set)
        const current = watchedValues[field.name];
        if (current === undefined || current === '') {
          setValue(field.name, field.defaultValue || '');
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues, currentStep]);

  const onSubmit = async (data: any) => {
    if (!formSchema) return;

    // If multi-step and not last step, go to next step
    if (formSchema.isMultiStep && currentStep < formSchema.steps.length - 1) {
      setCurrentStep(currentStep + 1);
      return;
    }

    // Final submission
    setSubmitting(true);

    try {
      if (!formSchema.id) {
        showError('Form ID not found');
        return;
      }

      // Collect all fields from all steps or flat fields
      const allFormFields = formSchema.isMultiStep
        ? formSchema.steps.flatMap((step) => step.fields)
        : formSchema.fields;

      // Convert any FileList/File values to base64
      const processedData: Record<string, any> = {};
      for (const [key, val] of Object.entries(data)) {
        if (val instanceof FileList && val.length > 0) {
          processedData[key] = await fileListToBase64(val);
        } else if (val instanceof File) {
          processedData[key] = await fileToBase64(val);
        } else {
          processedData[key] = val;
        }
      }

      // Prepare submission payload with all field values including calculated ones
      const submissionPayload = {
        data: processedData as Record<string, any>,
        metadata: {
          ...trackingData,
          submittedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
        },
      };

      console.log('📤 Submission Payload:', JSON.stringify(submissionPayload, null, 2));

      await formsApi.submit(formSchema.id, submissionPayload);

      setSubmitted(true);

      // Handle redirect
      if (formSchema.redirectUrl) {
        setTimeout(() => {
          window.location.href = formSchema.redirectUrl!;
        }, 2000);
      }
    } catch (err: any) {
      console.error('Submission Error:', err);
      showError(err.response?.data?.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !formSchema) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">404 Not Found</h2>
          <p className="text-gray-600">{error || 'The form you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-12 h-12 text-green-600" />
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Success!</h2>
          <p className="text-gray-600 whitespace-pre-line">
            {formSchema.thankYouMessage || 'Thank you for your submission!'}
          </p>
        </motion.div>
      </div>
    );
  }

  const isDark = formSchema.theme?.mode === 'dark';
  const totalSteps = formSchema.isMultiStep ? formSchema.steps.length : 1;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-6 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`w-full max-w-2xl rounded-xl shadow-2xl p-8 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className="mb-8">
          <h1
            className={`text-3xl font-bold mb-2 ${
              isDark ? 'text-white' : 'bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'
            }`}
          >
            {formSchema.title}
          </h1>
          {formSchema.description && (
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {formSchema.description}
            </p>
          )}
        </div>

        {/* Progress Bar (Multi-step) */}
        {formSchema.isMultiStep && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Step {currentStep + 1} of {totalSteps}
              </span>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {Math.round(progress)}%
              </span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
              />
            </div>
            {formSchema.steps[currentStep]?.title && (
              <h2 className={`text-xl font-semibold mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formSchema.steps[currentStep].title}
              </h2>
            )}
            {formSchema.steps[currentStep]?.description && (
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {formSchema.steps[currentStep].description}
              </p>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-12 gap-4"
            >
              {visibleFields.map((field) => (
                <DynamicField
                  key={field.id}
                  field={field}
                  register={register}
                  errors={errors}
                  value={watchedValues[field.name]}
                  theme={formSchema.theme?.mode}
                  watch={watch}
                  setValue={setValue}
                  allFields={currentFields}
                />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            {formSchema.isMultiStep && currentStep > 0 ? (
              <button
                type="button"
                onClick={handlePrevStep}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  isDark
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>
            ) : (
              <div />
            )}

            <button
              type="submit"
              disabled={submitting}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : formSchema.isMultiStep && currentStep < formSchema.steps.length - 1 ? (
                <>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>

        {/* Spam Protection Notice */}
        {formSchema.captchaEnabled && (
          <p className={`text-xs text-center mt-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            This form is protected by CAPTCHA
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default PublicFormPage;
